import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import type {
  ApBillListRow,
  ApBillStatus,
  ApBillWithLines,
  ApCreditDebitNoteListRow,
  ApPaymentRun,
  ApPaymentWithAllocations,
  CreateApBill,
  CreateApBillLine,
} from '@dcc-erp/shared';
import { CreateApBillSchema, fmt, fmtB, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { ApAgingTab } from '../components/ap/ApAgingTab';
import { ApBillApprovalPanel } from '../components/ap/ApBillApprovalPanel';
import { ApBillDetailDrawer } from '../components/ap/ApBillDetailDrawer';
import { ApDashboardTab } from '../components/ap/ApDashboardTab';
import { ApNoteApprovalPanel } from '../components/ap/ApNoteApprovalPanel';
import { ApNotesTab } from '../components/ap/ApNotesTab';
import { ApPaymentApprovalPanel } from '../components/ap/ApPaymentApprovalPanel';
import { ApPaymentRunsTab } from '../components/ap/ApPaymentRunsTab';
import { ApPaymentsTab } from '../components/ap/ApPaymentsTab';
import { CreateBillModal } from '../components/ap/CreateBillModal';
import {
  CreateCreditDebitNoteModal,
  type CreditDebitNoteBill,
} from '../components/ap/CreateCreditDebitNoteModal';
import { PaymentRunApprovalPanel } from '../components/ap/PaymentRunApprovalPanel';
import { PaymentRunWizard } from '../components/ap/PaymentRunWizard';
import { RecordPaymentModal } from '../components/ap/RecordPaymentModal';
import { RecurringTemplatesTab } from '../components/ap/RecurringTemplatesTab';
import {
  modalBody,
  modalBox,
  modalError,
  modalFooter,
  modalHeader,
  modalLabel,
  modalOverlay,
  modalTitle,
} from '../components/ar/modal-style';
import { DimensionFilter } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ImportResult,
  type ParsedUnit,
  type PreviewColumn,
} from '../components/SpreadsheetImportModal';
import {
  useActiveCompany,
  useActiveCompanyId,
  useBranches,
  useDepartments,
  useVendors,
} from '../lib/queries';
// AP-specific hooks live in their own module; coordinator wires the barrel
// re-export later. Direct import keeps this file independent of the index.
import {
  useApAging,
  useApBills,
  useApproveApBill,
  useCreateApBill,
  useRejectApBill,
} from '../lib/queries/ap';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

type ApTab =
  | 'all'
  | 'open'
  | 'pending'
  | 'overdue'
  | 'paid'
  | 'aging'
  | 'payments'
  | 'runs'
  | 'notes'
  | 'recurring'
  | 'dashboard';

const AP_TABS: readonly (readonly [ApTab, string])[] = [
  ['all', 'ทั้งหมด'],
  ['open', 'รอจ่าย'],
  ['pending', 'รออนุมัติ'],
  ['overdue', 'เกินกำหนด'],
  ['paid', 'จ่ายแล้ว'],
  ['aging', 'อายุหนี้'],
  ['payments', 'ประวัติการจ่าย'],
  ['runs', 'รอบจ่าย'],
  ['notes', 'ใบลด/เพิ่มหนี้'],
  ['recurring', 'บิลประจำ'],
  ['dashboard', 'แดชบอร์ด'],
];

// Tab → server filter mapping. POSTED + overdue=true is needed because
// "overdue" is a refinement of POSTED rows by dueDate, not a standalone
// status — server applies it so count + list are exact.
function filtersForTab(tab: ApTab): { status?: ApBillStatus; overdue?: boolean } {
  switch (tab) {
    case 'open':
      return { status: 'POSTED' };
    case 'pending':
      return { status: 'PENDING' };
    case 'paid':
      return { status: 'PAID' };
    case 'overdue':
      return { overdue: true };
    case 'all':
    default:
      return {};
  }
}

// AP Phase C1 — the early-payment discount window for a bill, derived from its
// snapshot (discountPercent / discountDays / billDate). discountDueDate =
// billDate + discountDays; in-window = nowMs ≤ discountDueDate AND a discount
// exists AND the bill is still unpaid. The list row only carries the snapshot
// (the server-derived inDiscountWindow lives on the findBill detail), so the
// list badge recomputes it here.
function discountWindow(
  bill: Pick<ApBillListRow, 'discountPercent' | 'discountDays' | 'billDate' | 'status'>,
  nowMs: number,
): { inWindow: boolean; percent: number; dueDate: Date } | null {
  const percent = toNumber(bill.discountPercent);
  if (percent <= 0) return null;
  const due = new Date(bill.billDate);
  if (Number.isNaN(due.getTime())) return null;
  due.setDate(due.getDate() + Number(bill.discountDays ?? 0));
  const inWindow = bill.status === 'POSTED' && nowMs <= due.getTime();
  return { inWindow, percent, dueDate: due };
}

// AP Phase C2 — map a bill list-row to the lean shape the credit/debit-note
// create modal needs. Outstanding = total + noteAdjustment − paid (the cap a
// CREDIT note can't exceed). vatRate seeds the note's default VAT rate.
function billToNoteBill(r: ApBillListRow): CreditDebitNoteBill {
  return {
    id: r.id,
    voucherNo: r.voucherNo,
    vatRate: r.vatRate,
    outstanding: toNumber(r.totalAmount) + toNumber(r.noteAdjustment) - toNumber(r.paidAmount),
  };
}

// ---- Spreadsheet import/export (นำเข้า / ส่งออก) ---------------------------
// One row = one AP bill carrying a SINGLE expense line. Multi-line bills are
// still created through the page's "บันทึกใบสำคัญจ่าย" modal. The export header
// keys match the import template columns so an exported file round-trips.

const AP_IMPORT_COLS = [
  'billDate',
  'dueDate',
  'vendorCode',
  'branchCode',
  'departmentCode',
  'vendorInvoiceNo',
  'description',
  'expenseCode',
  'amount',
  'vatRate',
  'currencyCode',
] as const;

const AP_IMPORT_FIELDS: ImportFieldSpec[] = [
  {
    col: 'billDate',
    label: 'วันที่บิล',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-05-29',
  },
  { col: 'dueDate', label: 'กำหนดจ่าย', required: true, hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-06-28' },
  {
    col: 'vendorCode',
    label: 'รหัสผู้ขาย',
    required: true,
    hint: 'รหัสผู้ขายในระบบ (ต้องมีอยู่จริง)',
  },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุต้องมีอยู่จริง',
  },
  {
    col: 'vendorInvoiceNo',
    label: 'เลขที่บิลผู้ขาย',
    required: false,
    hint: 'เว้นว่างได้ เช่น INV-2026-0042',
  },
  {
    col: 'description',
    label: 'คำอธิบาย',
    required: false,
    hint: 'เว้นว่างได้ — ใช้เป็นคำอธิบายรายการ',
  },
  {
    col: 'expenseCode',
    label: 'รหัสบัญชีค่าใช้จ่าย',
    required: true,
    hint: 'รหัสบัญชีค่าใช้จ่าย (COA) เช่น 5110',
  },
  {
    col: 'amount',
    label: 'จำนวนเงิน (ก่อน VAT)',
    required: true,
    hint: 'มากกว่า 0 — ยอดของรายการเดียว',
  },
  { col: 'vatRate', label: 'อัตรา VAT (%)', required: false, hint: 'เว้นว่างได้ — ค่าเริ่มต้น 7' },
  {
    col: 'currencyCode',
    label: 'สกุลเงิน',
    required: false,
    hint: 'เว้นว่างได้ — ค่าเริ่มต้น THB',
  },
];

const AP_TEMPLATE_EXAMPLES: (string | number)[][] = [
  [
    '2026-05-29',
    '2026-06-28',
    'V001',
    '',
    '',
    'INV-2026-0042',
    'ค่าวัตถุดิบ',
    '5110',
    10000,
    7,
    '',
  ],
];

export const Route = createFileRoute('/ap')({
  component: ApPage,
});

// ApPage signature — the prop is optional so the route can mount <ApPage /> with
// no args; B-core B2.5 passes onOpenRunWizard to open PaymentRunWizard seeded
// with the selected ids.
export function ApPage({ onOpenRunWizard }: { onOpenRunWizard?: (ids: string[]) => void } = {}) {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const [tab, setTab] = useState<ApTab>('all');
  const [q, setQ] = useState<string>('');
  const [filterBranchId, setFilterBranchId] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [paymentBill, setPaymentBill] = useState<ApBillListRow | null>(null);
  const [rejectBill, setRejectBill] = useState<ApBillListRow | null>(null);
  // Engine-managed bill whose central-approval (4-eyes) panel is open. Set from
  // a PENDING row that carries a non-null approvalStatus.
  const [engineBill, setEngineBill] = useState<ApBillListRow | null>(null);
  // Read-only bill detail drawer — opened by clicking the voucherNo cell.
  const [detailBillId, setDetailBillId] = useState<string | null>(null);
  // AP Phase C2 — the bill a credit/debit note is being created against (opens
  // CreateCreditDebitNoteModal), and the note whose engine approval panel is open.
  const [noteForBill, setNoteForBill] = useState<CreditDebitNoteBill | null>(null);
  const [notePanel, setNotePanel] = useState<ApCreditDebitNoteListRow | null>(null);

  // Row selection — feeds the batch toolbar's payment-run entry point.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // B2: cross-vendor payment-run wizard/panel state. The wizard opens seeded
  // with the rows matching the current selection; the panel shows a run's
  // engine approval chain. Both live here so the single A4 entry point can drive
  // them without a second button.
  const [runWizardSeed, setRunWizardSeed] = useState<ApBillListRow[] | null>(null);
  const [runPanel, setRunPanel] = useState<ApPaymentRun | null>(null);
  // M7 Phase 1b — single-bill payment engine 4-eyes panel. createPayment now
  // returns a DRAFT (no JV); RecordPaymentModal hands it up here so the panel
  // opens for submit → approve. Also reachable from the payments tab so a
  // DRAFT/PENDING/RETURNED payment row can be re-opened (compose-into-route).
  const [paymentPanel, setPaymentPanel] = useState<ApPaymentWithAllocations | null>(null);

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Approval mutation lives on the page (not the row) so we get a single
  // shared loading state — the row disables both buttons while a mutation
  // is in flight to avoid double-submits.
  const approve = useApproveApBill(companyId);

  // Masters for import (code → id) and export (id → code) mapping.
  const { data: vendors = [] } = useVendors(companyId, true);
  const { data: branches = [] } = useBranches(companyId, true);
  const { data: departments = [] } = useDepartments(companyId, true);
  const createBill = useCreateApBill(companyId);

  const vendorByCode = useMemo(
    () => new Map(vendors.map((v) => [v.code.toLowerCase(), v.id])),
    [vendors],
  );
  const vendorCodeById = useMemo(() => new Map(vendors.map((v) => [v.id, v.code])), [vendors]);
  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const deptByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  const tabFilters = filtersForTab(tab);
  const {
    data: bills,
    isPending,
    error,
  } = useApBills(companyId, {
    ...tabFilters,
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
  });

  // KPI aggregates + tab counts: totals across ALL statuses (not just the active
  // tab), but scoped to the active dimension filter so the KPI cards + tab counts
  // reflect the branch/department the user picked (spec §4.1 A4). Status stays
  // unfiltered here — only the dimension filter is threaded; this keeps the
  // payload bounded to the selected branch/department instead of the whole company.
  const { data: allBills } = useApBills(companyId, {
    ...(filterBranchId ? { branchId: filterBranchId } : {}),
    ...(filterDepartmentId ? { departmentId: filterDepartmentId } : {}),
  });

  const filteredRows = useMemo(() => {
    if (!bills) return [] as ApBillListRow[];
    if (!q) return bills;
    const needle = q.toLowerCase();
    return bills.filter(
      (r) =>
        r.voucherNo.toLowerCase().includes(needle) || r.vendorName.toLowerCase().includes(needle),
    );
  }, [bills, q]);

  // B2 (FIX BC-8): the single A4 "จ่ายเงินที่เลือก" entry point. Map the checked
  // bill ids → rows and seed the wizard. An external onOpenRunWizard prop (if
  // supplied by a host) is still invoked so existing wiring keeps working, but
  // the route mounts <ApPage /> with no prop, so the internal handler is what
  // actually opens the wizard. No second button is ever added.
  const handleOpenRunWizard = useCallback(
    (ids: string[]) => {
      onOpenRunWizard?.(ids);
      const idSet = new Set(ids);
      const rows = (bills ?? []).filter((b) => idSet.has(b.id));
      if (rows.length > 0) setRunWizardSeed(rows);
    },
    [onOpenRunWizard, bills],
  );

  // Select-all state: true only when filteredRows is non-empty and every row is selected.
  const allVisibleSelected =
    filteredRows.length > 0 && filteredRows.every((r) => selectedIds.has(r.id));

  function toggleAll() {
    setSelectedIds((prev) => {
      if (filteredRows.every((r) => prev.has(r.id))) return new Set();
      return new Set(filteredRows.map((r) => r.id));
    });
  }

  // Snapshot "now" once per render via useState's lazy initialiser (lint:
  // react-hooks/purity disallows Date.now() inside useMemo).
  const [nowMs] = useState<number>(() => Date.now());

  const totals = useMemo(() => {
    const list = allBills ?? [];
    let billed = 0;
    let paid = 0;
    let outstanding = 0;
    let overdue = 0;
    for (const r of list) {
      // EXTRA FIX: exclude VOID (cancelled) bills from all money KPIs so the
      // billed/paid/outstanding/overdue totals and the DPO numerator are consistent
      // (the DPO denominator already excludes VOID in the dpoDays memo above).
      if (r.status === 'VOID') continue;
      const total = toNumber(r.totalAmount);
      const paidAmt = toNumber(r.paidAmount);
      // AP Phase C2 — note-adjusted outstanding = total + noteAdjustment − paid
      // (CREDIT subtracts, DEBIT adds). noteAdjustment defaults to 0 so bills
      // with no notes behave exactly as before.
      const noteAdj = toNumber(r.noteAdjustment);
      const owed = total + noteAdj - paidAmt;
      billed += total;
      paid += paidAmt;
      outstanding += owed;
      const due = new Date(r.dueDate).getTime();
      if (r.status === 'POSTED' && due < nowMs) overdue += owed;
    }
    return { billed, paid, outstanding, overdue };
  }, [allBills, nowMs]);

  // Aging data — already built on the backend; consumed by the aging tab.
  const { data: aging } = useApAging(companyId);

  // Real DPO ≈ (current AP outstanding / trailing-365d purchases) × 365. Uses the
  // always-on allBills fetch (same source as totals). Guarded against /0 — returns
  // null when there are no trailing purchases so the KPI hides the figure.
  const dpoDays = useMemo(() => {
    const list = allBills ?? [];
    const windowStart = nowMs - 365 * 24 * 60 * 60 * 1000;
    let trailingPurchases = 0;
    for (const r of list) {
      const billed = new Date(r.billDate).getTime();
      if (billed >= windowStart && r.status !== 'VOID')
        trailingPurchases += toNumber(r.totalAmount);
    }
    if (trailingPurchases <= 0) return null;
    return Math.round((totals.outstanding / trailingPurchases) * 365);
  }, [allBills, nowMs, totals.outstanding]);

  // Per-tab counts. Same justification as ar.tsx — server-side count would
  // need 5 round-trips; aggregating from the cached full list is cheaper.
  const tabCounts = useMemo(() => {
    const list = allBills ?? [];
    return {
      all: list.length,
      open: list.filter((r) => r.status === 'POSTED').length,
      pending: list.filter((r) => r.status === 'PENDING').length,
      overdue: list.filter((r) => r.status === 'POSTED' && new Date(r.dueDate).getTime() < nowMs)
        .length,
      paid: list.filter((r) => r.status === 'PAID').length,
      aging: 0,
      payments: 0,
      runs: 0,
      notes: 0,
      recurring: 0,
      dashboard: 0,
    } satisfies Record<ApTab, number>;
  }, [allBills, nowMs]);

  // ---- Export -------------------------------------------------------------
  // Header keys mirror the import template columns so a file round-trips, then
  // append read-only computed columns (paidAmount/outstanding/status) at the end.
  const EXPORT_COLS: ExportColumn<ApBillListRow>[] = [
    { header: 'voucherNo', value: (r) => r.voucherNo, width: 16 },
    { header: 'vendorCode', value: (r) => vendorCodeById.get(r.vendorId) ?? '', width: 12 },
    { header: 'description', value: (r) => r.description ?? '', width: 30 },
    { header: 'billDate', value: (r) => String(r.billDate).slice(0, 10), width: 12 },
    { header: 'dueDate', value: (r) => String(r.dueDate).slice(0, 10), width: 12 },
    { header: 'subtotal', value: (r) => Number(r.subtotal), width: 14 },
    { header: 'vatRate', value: (r) => Number(r.vatRate), width: 10 },
    { header: 'totalAmount', value: (r) => Number(r.totalAmount), width: 14 },
    { header: 'paidAmount', value: (r) => Number(r.paidAmount), width: 14 },
    {
      header: 'outstanding',
      value: (r) => Number(r.totalAmount) - Number(r.paidAmount),
      width: 14,
    },
    { header: 'status', value: (r) => r.status, width: 10 },
  ];

  async function handleExport() {
    await exportRows(
      `เจ้าหนี้การค้า_${company?.code ?? 'COMPANY'}_${todayStamp()}.xlsx`,
      'เจ้าหนี้การค้า',
      EXPORT_COLS,
      filteredRows,
    );
  }

  // ---- Import parse: one row = one bill with a single expense line --------
  function parse(grid: string[][]): {
    units: ParsedUnit<CreateApBill>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of ['billDate', 'dueDate', 'vendorCode', 'expenseCode', 'amount']) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreateApBill>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const vendorCode = cell(cols, 'vendorCode');
      const vendorId = vendorCode ? vendorByCode.get(vendorCode.toLowerCase()) : undefined;
      if (!vendorId) {
        units.push({ rowNo, data: null, error: `ไม่พบผู้ขาย: ${vendorCode}` });
        continue;
      }

      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : undefined;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }

      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? deptByCode.get(departmentCode.toLowerCase())
        : undefined;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const vendorInvoiceNo = cell(cols, 'vendorInvoiceNo');
      const description = cell(cols, 'description');
      const vatRaw = cell(cols, 'vatRate');
      const currencyCode = cell(cols, 'currencyCode');
      const amount = Number(cell(cols, 'amount'));

      const line: CreateApBillLine = {
        description: description || cell(cols, 'expenseCode'),
        qty: 1,
        unitPrice: amount,
        expenseCode: cell(cols, 'expenseCode'),
      };

      const candidate = {
        billDate: cell(cols, 'billDate'),
        dueDate: cell(cols, 'dueDate'),
        vendorId,
        ...(branchId ? { branchId } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(vendorInvoiceNo ? { vendorInvoiceNo } : {}),
        ...(description ? { description } : {}),
        vatRate: vatRaw ? Number(vatRaw) : 7,
        ...(currencyCode ? { currencyCode } : {}),
        lines: [line],
      };

      const parsed = CreateApBillSchema.safeParse(candidate);
      units.push(
        parsed.success
          ? { rowNo, data: parsed.data, error: null }
          : { rowNo, data: null, error: parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreateApBill>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };

    // Build a deterministic, ASCII-safe Idempotency-Key per row so that
    // re-importing the exact same file deduplicates on the server instead of
    // minting a second ApBill / second JV.
    //
    // Key strategy: derive from stable row content (companyId + vendorId +
    // billDate + dueDate + expenseCode + amount + vendorInvoiceNo).  To
    // handle genuinely distinct rows with identical content (e.g. two lines
    // with no invoice no, same vendor, same amount, same date), we track
    // a per-fingerprint occurrence counter and fold it in.  This preserves
    // dedup on re-import while keeping legit in-file duplicates distinct.
    //
    // Residual trade-off: if such identical rows are reordered between
    // imports their occurrence indices shift, causing new keys → the server
    // creates new bills.  This is the correct behaviour (we cannot tell
    // which row mapped to which bill after a reorder), and it is no worse
    // than the pre-fix behaviour (which always created new bills).
    //
    // encodeURIComponent → all-ASCII so the value is safe as an HTTP
    // header (a raw Thai vendorInvoiceNo would throw at fetch time).
    const occurrenceCount = new Map<string, number>();
    function rowKey(d: CreateApBill): string {
      const amount = String(d.lines[0]?.unitPrice ?? '');
      const expCode = d.lines[0]?.expenseCode ?? '';
      const raw = [
        companyId ?? '',
        d.vendorId,
        d.billDate,
        d.dueDate,
        expCode,
        amount,
        d.vendorInvoiceNo ?? '',
      ].join('|');
      const count = (occurrenceCount.get(raw) ?? 0) + 1;
      occurrenceCount.set(raw, count);
      return encodeURIComponent(raw) + '_' + String(count);
    }

    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createBill.mutateAsync({ data: u.data, idempotencyKey: rowKey(u.data) });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const IMPORT_PREVIEW_COLS: PreviewColumn<CreateApBill>[] = [
    {
      header: 'ผู้ขาย',
      width: 90,
      render: (d) => vendorCodeById.get(d.vendorId) ?? '',
    },
    { header: 'วันที่บิล', width: 100, render: (d) => d.billDate },
    { header: 'กำหนดจ่าย', width: 100, render: (d) => d.dueDate },
    { header: 'บัญชี', width: 80, render: (d) => d.lines[0]?.expenseCode ?? '' },
    {
      header: 'จำนวนเงิน',
      width: 100,
      render: (d) => fmt(Number(d.lines[0]?.unitPrice ?? 0), 2),
    },
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้ / เจ้าหนี้', 'เจ้าหนี้การค้า']}
        title="เจ้าหนี้การค้า (Accounts Payable)"
        actions={
          <>
            <HelpButton guide="ap" />
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={filteredRows.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button
              className="btn"
              onClick={() => setTab('pending')}
              title="แสดงใบสำคัญที่รออนุมัติ"
            >
              <Icon name="check" size={14} /> อนุมัติ ({tabCounts.pending})
            </button>
            <button className="btn primary" onClick={() => setCreateOpen(true)}>
              <Icon name="plus" size={14} /> บันทึกใบสำคัญจ่าย
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ใบสำคัญรวม
          </div>
          <div className="value tnum">
            {fmtB(totals.billed)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{allBills?.length ?? 0} ฉบับ · YTD</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            จ่ายแล้ว
          </div>
          <div className="value tnum">
            {fmtB(totals.paid)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} />{' '}
            {totals.billed > 0 ? Math.round((totals.paid / totals.billed) * 100) : 0}%
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            คงค้างจ่าย
          </div>
          <div className="value tnum">
            {fmtB(totals.outstanding)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {dpoDays != null ? `DPO เฉลี่ย ${dpoDays} วัน` : 'DPO — ยังไม่มีข้อมูล'}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            เกินกำหนด
          </div>
          <div className="value tnum">
            {fmtB(totals.overdue)} <span className="unit">บาท</span>
          </div>
          <div className="trend down">{tabCounts.overdue} ฉบับ</div>
        </div>
      </div>

      <div className="page-tabs">
        {AP_TABS.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}{' '}
            <span className="faint" style={{ marginLeft: 4 }}>
              ({tabCounts[k]})
            </span>
          </div>
        ))}
      </div>

      {tab === 'aging' ? (
        <ApAgingTab aging={aging} companyCode={company?.code ?? 'COMPANY'} />
      ) : tab === 'payments' ? (
        <ApPaymentsTab
          companyId={companyId}
          vendors={vendors}
          companyCode={company?.code ?? 'COMPANY'}
          onOpenPanel={setPaymentPanel}
        />
      ) : tab === 'runs' ? (
        <ApPaymentRunsTab companyId={companyId} onOpenRun={setRunPanel} />
      ) : tab === 'notes' ? (
        <ApNotesTab companyId={companyId} onOpenNote={setNotePanel} />
      ) : tab === 'recurring' ? (
        // AP Phase C4 — recurring vendor-bill template management.
        <RecurringTemplatesTab companyId={companyId} />
      ) : tab === 'dashboard' ? (
        // AP Phase C7a — read-only KPI dashboard.
        <ApDashboardTab companyId={companyId} />
      ) : (
        <div className="tbl-shell">
          <div className="filter-row">
            <input
              className="input search w-lg"
              placeholder="ค้นหา เลขที่ใบสำคัญ / ผู้ขาย"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                paddingLeft: 28,
                backgroundImage: SEARCH_BG,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '8px center',
              }}
            />
            <DimensionFilter
              companyId={companyId}
              branchId={filterBranchId}
              departmentId={filterDepartmentId}
              onBranchChange={setFilterBranchId}
              onDepartmentChange={setFilterDepartmentId}
            />
          </div>
          {selectedIds.size > 0 && (
            <div
              className="filter-row"
              style={{
                background: 'var(--surface-2, #f4f6fb)',
                borderRadius: 8,
                padding: '8px 12px',
                gap: 12,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                เลือกแล้ว {selectedIds.size} ฉบับ
              </span>
              <button
                type="button"
                className="btn primary"
                data-testid="open-run-wizard"
                onClick={() => handleOpenRunWizard([...selectedIds])}
              >
                <Icon name="check" size={14} /> จ่ายเงินที่เลือก
              </button>
              <button type="button" className="btn ghost" onClick={() => setSelectedIds(new Set())}>
                ล้างการเลือก
              </button>
            </div>
          )}
          <div className="tbl-wrap" style={{ maxHeight: 480 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 24 }}>
                    <input
                      type="checkbox"
                      aria-label="เลือกทั้งหมด"
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>เลขที่ใบสำคัญ</th>
                  <th>ผู้ขาย / ผู้รับเงิน</th>
                  <th>รายละเอียด</th>
                  <th>วันที่</th>
                  <th>กำหนดจ่าย</th>
                  <th className="num">มูลค่ารวม</th>
                  <th className="num">จ่ายแล้ว</th>
                  <th className="num">คงค้าง</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {isPending && (
                  <tr>
                    <td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                      กำลังโหลด…
                    </td>
                  </tr>
                )}
                {error && (
                  <tr>
                    <td colSpan={10} className="neg" style={{ textAlign: 'center', padding: 20 }}>
                      โหลดไม่สำเร็จ: {error.message}
                    </td>
                  </tr>
                )}
                {!isPending && !error && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                      ไม่พบใบสำคัญจ่าย
                    </td>
                  </tr>
                )}
                {filteredRows.map((r) => {
                  const total = toNumber(r.totalAmount);
                  const paid = toNumber(r.paidAmount);
                  // Note-adjusted คงค้าง (AP Phase C2): total + noteAdjustment − paid.
                  const outstanding = total + toNumber(r.noteAdjustment) - paid;
                  const isOverdue = r.status === 'POSTED' && new Date(r.dueDate).getTime() < nowMs;
                  // AP Phase C1 — early-payment discount window badge (in-window bills only).
                  const disc = discountWindow(r, nowMs);
                  return (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`เลือก ${r.voucherNo}`}
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleRow(r.id)}
                        />
                      </td>
                      <td className="code">
                        <button
                          type="button"
                          onClick={() => setDetailBillId(r.id)}
                          style={{
                            color: 'var(--brand)',
                            fontWeight: 500,
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            font: 'inherit',
                          }}
                        >
                          {r.voucherNo}
                        </button>
                        {disc?.inWindow && (
                          <span
                            className="badge positive"
                            data-testid={`discount-badge-${r.id}`}
                            style={{ marginLeft: 6, fontSize: 11 }}
                            title="อยู่ในช่วงรับส่วนลดจ่ายเร็ว"
                          >
                            ส่วนลด {disc.percent}% ถึง {fmtDate(disc.dueDate)}
                          </span>
                        )}
                      </td>
                      <td>{r.vendorName}</td>
                      <td className="muted">{r.description ?? '—'}</td>
                      <td>{fmtDate(r.billDate)}</td>
                      <td className={isOverdue ? 'neg' : ''}>{fmtDate(r.dueDate)}</td>
                      <td className="num tnum">{fmt(total)}</td>
                      <td className="num tnum">{paid > 0 ? fmt(paid) : '—'}</td>
                      <td className="num tnum">
                        <b>{fmt(outstanding)}</b>
                      </td>
                      <td>
                        {r.status === 'PAID' ? (
                          <span className="badge positive">
                            <span className="bdot" />
                            จ่ายแล้ว
                          </span>
                        ) : r.status === 'VOID' ? (
                          <span className="badge neutral">
                            <span className="bdot" />
                            ยกเลิก
                          </span>
                        ) : r.status === 'PENDING' ? (
                          <span className="badge warning">
                            <span className="bdot" />
                            รออนุมัติ
                          </span>
                        ) : isOverdue ? (
                          <span className="badge negative">
                            <span className="bdot" />
                            เกินกำหนด
                          </span>
                        ) : (
                          <span className="badge info">
                            <span className="bdot" />
                            รอจ่าย
                          </span>
                        )}{' '}
                        {r.status === 'POSTED' && (
                          <button
                            className="btn ghost"
                            style={{ marginLeft: 8, padding: '2px 8px', fontSize: 12 }}
                            onClick={() => setPaymentBill(r)}
                          >
                            จ่ายเงิน
                          </button>
                        )}
                        {r.status === 'POSTED' && (
                          <button
                            className="btn ghost"
                            style={{ marginLeft: 4, padding: '2px 8px', fontSize: 12 }}
                            title="ออกใบลดหนี้ / ใบเพิ่มหนี้"
                            onClick={() => setNoteForBill(billToNoteBill(r))}
                          >
                            ใบลด/เพิ่มหนี้
                          </button>
                        )}
                        {r.status === 'PENDING' && (
                          <ApBillRowActions
                            row={r}
                            approveBusy={approve.isPending}
                            onApproveManual={() => approve.mutate(r.id)}
                            onReject={() => setRejectBill(r)}
                            onEngine={() => setEngineBill(r)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length > 0 && (
                  <tr className="total">
                    <td colSpan={6}>รวม {filteredRows.length} ฉบับ</td>
                    <td className="num tnum">
                      {fmt(filteredRows.reduce((a, r) => a + toNumber(r.totalAmount), 0))}
                    </td>
                    <td className="num tnum">
                      {fmt(filteredRows.reduce((a, r) => a + toNumber(r.paidAmount), 0))}
                    </td>
                    <td className="num tnum">
                      {fmt(
                        filteredRows.reduce(
                          (a, r) =>
                            a +
                            (toNumber(r.totalAmount) +
                              toNumber(r.noteAdjustment) -
                              toNumber(r.paidAmount)),
                          0,
                        ),
                      )}
                    </td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && companyId && (
        <CreateBillModal
          companyId={companyId}
          onClose={() => setCreateOpen(false)}
          onOpenBill={(id) => {
            setCreateOpen(false);
            setDetailBillId(id);
          }}
        />
      )}
      {showImport && companyId && (
        <SpreadsheetImportModal<CreateApBill>
          title="นำเข้าใบสำคัญจ่าย (เจ้าหนี้)"
          intro={
            <>
              นำเข้าได้ครั้งละ 1 แถว = ใบสำคัญจ่าย 1 ฉบับ ที่มี<b>รายการค่าใช้จ่ายรายการเดียว</b> —
              ใบสำคัญที่มีหลายรายการให้ใช้ปุ่ม “บันทึกใบสำคัญจ่าย” แทน. รหัสผู้ขาย / สาขา / หน่วยงาน
              ต้องมีอยู่ในระบบก่อน
            </>
          }
          fields={AP_IMPORT_FIELDS}
          templateColumns={[...AP_IMPORT_COLS]}
          templateExamples={AP_TEMPLATE_EXAMPLES}
          templateFilename="ap_bill_template.xlsx"
          sheetName="เจ้าหนี้การค้า"
          parse={parse}
          previewColumns={IMPORT_PREVIEW_COLS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
      {paymentBill && companyId && (
        <RecordPaymentModal
          companyId={companyId}
          bill={paymentBill}
          onCreated={(p) => setPaymentPanel(p)}
          onClose={() => setPaymentBill(null)}
        />
      )}
      {paymentPanel && companyId && (
        <ApPaymentApprovalPanel
          companyId={companyId}
          payment={paymentPanel}
          onClose={() => setPaymentPanel(null)}
        />
      )}
      {rejectBill && companyId && (
        <RejectBillModal
          companyId={companyId}
          bill={rejectBill}
          onClose={() => setRejectBill(null)}
        />
      )}
      {engineBill && companyId && (
        <ApBillApprovalPanel
          companyId={companyId}
          // The list row lacks lines/approvals; the panel re-fetches the full
          // detail via useApBill and uses this row only as a header placeholder
          // while loading. The cast documents that narrowing.
          bill={engineBill as unknown as ApBillWithLines}
          onClose={() => setEngineBill(null)}
        />
      )}
      {detailBillId && companyId && (
        <ApBillDetailDrawer
          companyId={companyId}
          billId={detailBillId}
          onClose={() => setDetailBillId(null)}
          onCreateNote={setNoteForBill}
          onOpenNote={setNotePanel}
        />
      )}
      {runWizardSeed && companyId && (
        <PaymentRunWizard
          companyId={companyId}
          preselected={runWizardSeed}
          onClose={() => {
            setRunWizardSeed(null);
            setSelectedIds(new Set());
          }}
        />
      )}
      {runPanel && companyId && (
        <PaymentRunApprovalPanel
          companyId={companyId}
          run={runPanel}
          onClose={() => setRunPanel(null)}
        />
      )}
      {/* AP Phase C2 — create a credit/debit note from a POSTED bill. */}
      {noteForBill && companyId && (
        <CreateCreditDebitNoteModal bill={noteForBill} onClose={() => setNoteForBill(null)} />
      )}
      {/* AP Phase C2 — engine approval panel for a note (from the notes tab or
          the drawer's note list). */}
      {notePanel && companyId && (
        <ApNoteApprovalPanel
          companyId={companyId}
          note={notePanel}
          onClose={() => setNotePanel(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// PENDING-row action cell. Engine-managed bills (approvalStatus != null) route
// through the central 4-eyes approval engine — the manual อนุมัติ/ปฏิเสธ buttons
// are backend-blocked for them (approveBill/rejectBill throw), so we show a
// single "ดำเนินการอนุมัติ" entry that opens ApBillApprovalPanel. Manual bills
// (approvalStatus == null) keep the original approve/reject buttons verbatim.
// =============================================================================

interface ApBillRowActionsProps {
  row: ApBillListRow;
  approveBusy: boolean;
  onApproveManual: () => void;
  onReject: () => void;
  onEngine: () => void;
}

export function ApBillRowActions({
  row,
  approveBusy,
  onApproveManual,
  onReject,
  onEngine,
}: ApBillRowActionsProps) {
  if (row.approvalStatus != null) {
    return (
      <button
        className="btn primary"
        style={{ marginLeft: 8, padding: '2px 8px', fontSize: 12 }}
        onClick={onEngine}
        title="ดำเนินการอนุมัติ 4 ตา (สายอนุมัติกลาง)"
      >
        <Icon name="check" size={12} /> ดำเนินการอนุมัติ
      </button>
    );
  }
  return (
    <>
      <button
        className="btn primary"
        style={{ marginLeft: 8, padding: '2px 8px', fontSize: 12 }}
        disabled={approveBusy}
        onClick={onApproveManual}
        title="อนุมัติและสร้าง JV"
      >
        <Icon name="check" size={12} /> อนุมัติ
      </button>
      <button
        className="btn ghost"
        style={{
          marginLeft: 4,
          padding: '2px 8px',
          fontSize: 12,
          color: 'var(--negative)',
        }}
        disabled={approveBusy}
        onClick={onReject}
        title="ปฏิเสธใบสำคัญ"
      >
        <Icon name="x" size={12} /> ปฏิเสธ
      </button>
    </>
  );
}

// =============================================================================
// Modal: Reject AP Bill — captures a free-text reason that gets stamped into
// the bill's description field. Status flips to VOID (re-used as the terminal
// "rejected before posting" state — see ap-bills.service.rejectBill()).
// =============================================================================

interface RejectBillModalProps {
  companyId: string;
  bill: ApBillListRow;
  onClose: () => void;
}

function RejectBillModal({ companyId, bill, onClose }: RejectBillModalProps) {
  const reject = useRejectApBill(companyId);
  const [reason, setReason] = useState('');

  async function handleSubmit() {
    await reject.mutateAsync({ id: bill.id, reason: reason.trim() });
    onClose();
  }

  const disabled = reason.trim().length === 0 || reject.isPending;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, width: 460 }}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>ปฏิเสธใบสำคัญ — {bill.voucherNo}</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={modalBody}>
          <div className="muted" style={{ fontSize: 14 }}>
            ผู้ขาย: <b>{bill.vendorName}</b>
          </div>
          <div className="muted" style={{ fontSize: 14 }}>
            มูลค่ารวม: <b className="tnum">{fmt(toNumber(bill.totalAmount), 2)}</b> บาท
          </div>
          <div>
            <label style={modalLabel}>เหตุผลในการปฏิเสธ</label>
            <textarea
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น เอกสารแนบไม่ครบ / ราคาผิด / รออนุมัติงบประมาณ"
              rows={4}
              style={{ width: '100%', fontSize: 14, padding: 10, resize: 'vertical' }}
            />
          </div>
          {reject.error && <div style={modalError}>ปฏิเสธไม่สำเร็จ: {reject.error.message}</div>}
        </div>
        <div style={modalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn"
            disabled={disabled}
            style={{
              background: 'var(--negative)',
              color: 'white',
              borderColor: 'var(--negative)',
            }}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="x" size={14} /> {reject.isPending ? 'กำลังปฏิเสธ…' : 'ยืนยันการปฏิเสธ'}
          </button>
        </div>
      </div>
    </div>
  );
}
